/**
 * Derives the SearchResults view flags (loading / result / download / save /
 * outreach gating + the paginated-view booleans) from the store `state` plus the
 * three sibling stores those flags read.
 *
 * Extracted verbatim from the inline `const isLoading = …` … block in
 * SearchResults/index.jsx (TD-2377 — SonarQube cognitive-complexity cleanup,
 * Phase 3 Part B). That block's `&&` / `||` / ternary chains counted directly
 * against the component arrow's cognitive complexity (unlike JSX `{cond && …}`
 * gates, plain-JS logical operators are scored); pulling them into this pure
 * function lifts them out of the component. Reads happen when the component
 * (an `observer`) calls this during render, so MobX reactivity is unchanged.
 */
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { isProfilerListVisible } from './constants/constants';
import { PAGE_SIZE as pageSize } from './getResultsHelpers';

const SEARCH_TYPE = UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE];

export const deriveSearchResultsFlags = (
  state,
  { projectStore, profilerProjectStore, outreachStore }
) => {
  const isLoading = state.isLoading || state.isInitialLoading;
  const hasResults = !!state.results.length;
  const hasError = !!state.resultsError;

  const showNoResults = !isLoading && !hasError && !hasResults;
  const showResults = !isLoading && !hasError && hasResults;
  const showPaging =
    showResults &&
    // TD-2200 V1 — gate on the filtered set actually rendered in the List
    // view (`filteredAndSortedResults`), not the raw `results` count.
    state.filteredAndSortedResults.length > pageSize &&
    // TD-2720 — the stacked heatmap view has a list, so it has a pager too.
    (state.searchType[0] !== SEARCH_TYPE.Profiler ||
      (state.searchType[0] === SEARCH_TYPE.Profiler && isProfilerListVisible(state.profilerView)));

  const canDownload = ['number', 'string'].includes(typeof state.searchId) && hasResults;
  const disableSaveSearch = !canDownload || projectStore.isLoadingProjects;
  const disableProfilerSaveSearch = !canDownload || profilerProjectStore.isLoadingProjects;
  const canSaveSearch = canDownload && !state.projectId;
  const fillSaveSearchIcon = !!state.projectId;
  const disableMassOutreach =
    !state.projectId ||
    !hasResults ||
    projectStore.isLoadingProjects ||
    outreachStore.isSendingOutreach;

  const excludedResults = state.results.filter((entry) => entry.isExcluded);
  const currentSearchType = state.loadingSearchType?.[0] || state.searchType?.[0];

  return {
    isLoading,
    hasResults,
    hasError,
    showDetailsPopup: !!state.detailsPopupDisplayed,
    showMessagePopup: !!state.messagePopupDisplayed,
    showMassOutreachPopup: !!state.massOutreachPopupDisplayed,
    showSaveSearchPopup: !!state.saveSearchPopupDisplayed,
    showProfilerSaveSearchPopup: !!state.saveProfilerSearchPopupDisplayed,
    showNoResults,
    showResults,
    showPaging,
    canDownload,
    disableSaveSearch,
    disableProfilerSaveSearch,
    canSaveSearch,
    fillSaveSearchIcon,
    disableMassOutreach,
    excludedResults,
    currentSearchType,
    profilerColumns: state.profilerColumns
  };
};
